import { AddRounded, Close, ContentCopy } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { IContractSchema } from '../../definitions/ContractSchema'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import { Cell, Column, Row } from '../controls/table'
import SmartTable from '../controls/table/SmartTable'
import useCommand from '../hooks/useCommand'
import PermissionsWrapper from '../permissions/PermissionsWrapper'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'

interface P {
    openSchemaDialog?: boolean
    onCloseDialog?: () => void
}

function ContractsSchema({ openSchemaDialog, onCloseDialog }: P) {
    const dispatch = useAppDispatch()
    const [openDialog, setOpenDialog] = useState(false)
    const [nameError, setNameError] = useState(false)
    const [newSchemaName, setNewSchemaName] = useState<IContractSchema['name']>('')

    useEffect(() => {
        if (openSchemaDialog) {
            handleOpenCreateDialog()
            if (onCloseDialog) onCloseDialog()
        }
    }, [openSchemaDialog])

    const handleNavigateToContractSchemaEditor = (uuid: string) => {
        dispatch(changeRoute({
            route: 'contract-schema-editor',
            params: { uuid }
        }))
    }

    const handleRowClick = (indexRow: number, cells: Cell[], row: Row, item: IContractSchema) => {
        if (item) {
            dispatch(changeRoute({
                route: 'contract-schema-editor',
                params: { uuid: item.uuid }
            }))
        } else {
            console.error('Items not found in the rows.')
        }
    }

    useEffect(() => {
        if (openSchemaDialog) {
            handleOpenCreateDialog()
            if (onCloseDialog) onCloseDialog()
        }
    }, [openSchemaDialog])

    const handleOpenCreateDialog = async () => {
        setOpenDialog(true)
    }

    const { execute: executeCreate, status: createStatus } = useCommand({
        commandFn: async (newSchemaName: IContractSchema['name']) => {
            if (!newSchemaName.trim()) {
                setNameError(true)
                snackbar.show({ severity: 'error', text: 'Schema name cannot be empty.' })
                throw new Error('Schema name cannot be empty')
            }

            setNameError(false)
            try {
                const createdSchema = await ApiClient.createContractSchema(newSchemaName)
                snackbar.show({ severity: 'success', text: 'New Schema created successfully!' })
                setOpenDialog(false)
                handleNavigateToContractSchemaEditor(createdSchema.uuid)
                return createdSchema
            } catch (error) {
                console.error('Failed to create schema:', error)
                snackbar.show({ severity: 'error', text: 'Unable to create schema' })
                throw error
            }
        }
    })

    const { execute: executeClone } = useCommand({
        commandFn: async (item: IContractSchema) => {
            modals.openConfirm({
                title: 'Confirm Schema Cloning',
                startIcon: <ContentCopy />,
                children: <p>Are you sure you want to clone this schema?</p>,
                labels: { confirm: 'Clone', cancel: 'Cancel' },
                colors: { confirm: 'primary', cancel: 'primary' },
                onConfirm: async () => {
                    try {
                        const response = await ApiClient.cloneContractSchema(item.uuid)
                        snackbar.show({ severity: 'success', text: `Schema ${response.name} created successfully!` })
                        handleNavigateToContractSchemaEditor(response.uuid)
                    } catch (error) {
                        console.error('Failed to clone:', error)
                        snackbar.show({ severity: 'error', text: `Unable to clone: ${error}` })
                    }
                }
            })
        }
    })

    const columns: Column<IContractSchema>[] = [
        { id: 'name', label: 'Name' },
        { id: 'createdAt', label: 'Date created',
            customRenderer: cell => {
                const dateStr = (cell.value as string)?.slice(0, 10)
                return <>{dateStr}</>
            }
        },
        {
            id: 'createdByUserName',
            label: 'Created By',
            customRenderer: cell => {
                if (cell.value) return <Typography>{cell.value}</Typography>
            }
        },
        { id: 'isDefault', label: 'Type',
            customRenderer: cell => {
                if (cell.value) {
                    return <Chip label='Default' />
                }
            }
        },
        {
            id: 'uuid',
            label: 'Actions',
            isPK: true,
            customRenderer: (cell, row, item) => {
                return (
                    <Box>
                        <PermissionsWrapper permission='contract-schema.create'>
                            <IconButton
                                onClick={e => {
                                    e.stopPropagation()
                                    executeClone(item)
                                }}>
                                <Tooltip title='Clone' arrow>
                                    <ContentCopy />
                                </Tooltip>
                            </IconButton>
                        </PermissionsWrapper>
                    </Box>
                )
            }
        }

    ]

    return (
        <>
            <SmartCard table={true}>
                <SmartTable<IContractSchema>
                    toolbar={{
                        columns: true,
                        filters: true,
                        search: ['name']
                    }}
                    columns={columns}
                    noCollapse
                    datasource={{
                        remote: {
                            url: API_URLS.os.getContractSchemas,
                            onSuccess: response => {
                                return { ...response, items: response.items } as any
                            }
                        }
                    }}
                    onClickRow={handleRowClick}
                />
            </SmartCard>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth='sm'>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!nameError) {
                        executeCreate(newSchemaName)
                    }
                }}>
                    <DialogTitle p={1}>
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography variant='h6'>Create New Schema</Typography>
                            <IconButton
                                onClick={() => {
                                    setOpenDialog(false)
                                    setNewSchemaName('')
                                }}
                                size='small'>
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent>
                        <TextField
                            label='Schema Name'
                            fullWidth
                            margin='normal'
                            value={newSchemaName}
                            onChange={e => {
                                setNewSchemaName(e.target.value)
                                setNameError(false)
                            }}
                            placeholder='e.g. My New Contract Schema'
                            error={nameError}
                            helperText={nameError ? 'Schema name cannot be empty' : ''}
                            autoFocus
                        />
                    </DialogContent>

                    <DialogActions style={{ paddingBottom: '16px', paddingRight: '16px' }}>
                        <LoadingButton
                            startIcon={<AddRounded />}
                            loadingPosition='start'
                            variant='contained'
                            type='submit'
                            loading={createStatus === 'loading'}
                            disabled={nameError}>
                            Create
                        </LoadingButton>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    )
}

export default ContractsSchema
